import { useCallback, useEffect, useState } from 'react';
import {
    FormControl,
    FormLabel,
    Input,
    Select,
    MenuItem,
    Switch,
    Slider,
    Stack,
    FormControlLabel,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Optimizer, SupportedBuildingFunctionDtoWithName } from '../../pv-calculator.dto';
import LoadProfilesContainer from './LoadProfilesContainer';
import CHPProducer from './CHPProducer';
import { OptimizerMode, PVInputParams, getFriendlyOptimizerName } from '../../pv-input-types';
import { resolveBuildingFunctionCode } from 'src/common/buildingFunctions';

export default function PVInput(props: {
    params: PVInputParams;
    result?: { kwp: number; battery_capacity_factor: number };
    supportedBuildingFunctions: SupportedBuildingFunctionDtoWithName[];
    costModels: string[];
    onChange: (params: PVInputParams) => void;
}) {
    const { t } = useTranslation();

    const [params, setParams] = useState<PVInputParams>(props.params);

    const updateParams = useCallback(
        (newParams: Partial<PVInputParams>) => {
            const updatedParams = { ...params, ...newParams };
            setParams(updatedParams);
            props.onChange(updatedParams);
        },
        [params, props],
    );

    // When switching to manual, use the values from the result
    useEffect(() => {
        if (params.productionOptimizerMode === OptimizerMode.Manual && props.result) {
            updateParams({ targetKwp: props.result.kwp });
        }
    }, [params.productionOptimizerMode, props.result, updateParams]);
    useEffect(() => {
        if (params.batteryOptimizerMode === OptimizerMode.Manual && props.result) {
            updateParams({ batteryKwpFactor: props.result.battery_capacity_factor });
        }
    }, [params.batteryOptimizerMode, props.result, updateParams]);

    useEffect(() => {
        if (props.params) {
            setParams(props.params);
        }
    }, [props.params]);

    const isFullFeedIn = params.optimizer === Optimizer.FULL_FEED_IN;
    const selectedBuildingFunction = props.supportedBuildingFunctions.find(
        (buildingFunction) => buildingFunction.code === params.buildingFunctionCode,
    );

    return (
        <Stack spacing={1}>
            <FormControl required>
                <FormLabel>{t('Optimizer')}</FormLabel>
                <Select
                    size="small"
                    value={params.optimizer}
                    onChange={(e) => updateParams({ optimizer: e.target.value as Optimizer })}
                >
                    {Object.values(Optimizer).map((o: Optimizer) => (
                        <MenuItem key={o} value={o}>
                            {t(getFriendlyOptimizerName(o))}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl required>
                <FormLabel>{t('Building Function')}</FormLabel>
                <Select
                    size="small"
                    value={params.buildingFunctionCode}
                    onChange={(e) => updateParams({ buildingFunctionCode: e.target.value as string })}
                >
                    {props.supportedBuildingFunctions.map((buildingFunction) => (
                        <MenuItem key={buildingFunction.code} value={buildingFunction.code}>
                            {buildingFunction.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {selectedBuildingFunction && selectedBuildingFunction.mappedTo && (
                <Alert severity="warning">
                    {t(
                        'This building function refers to another building function ({{mappedTo}}), because it does not have its own load profile. Therefore, the results may not be highly accurate.',
                        {
                            mappedTo: resolveBuildingFunctionCode(selectedBuildingFunction.mappedTo),
                        },
                    )}
                </Alert>
            )}

            <FormControl required>
                <FormLabel>{t('Cost Model')}</FormLabel>
                <Select
                    size="small"
                    value={params.costModel}
                    onChange={(e) => updateParams({ costModel: e.target.value as string })}
                >
                    {props.costModels.map((model) => (
                        <MenuItem key={model} value={model}>
                            {model}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl required>
                <FormLabel>{t('Funding rate (0 - 100 %)')}</FormLabel>
                <Input
                    type="number"
                    value={params.fundingRate}
                    onChange={(e) => updateParams({ fundingRate: parseFloat(e.target.value) })}
                />
            </FormControl>

            <FormControl required>
                <FormLabel>{t('Energy price (in €/kWh)')}</FormLabel>
                <Input
                    type="number"
                    value={params.energyPrice}
                    onChange={(e) => updateParams({ energyPrice: parseFloat(e.target.value) })}
                    slotProps={{ input: { min: 0.1, max: 1.0, step: 0.01 } }}
                />
            </FormControl>

            <FormLabel>{t('Load profiles')}</FormLabel>
            <LoadProfilesContainer
                value={params.loadProfiles}
                onChange={(value) => updateParams({ loadProfiles: value })}
            />

            <CHPProducer onChange={(value) => updateParams({ chpProductionProfile: value })} />

            {params.optimizer !== Optimizer.FULL_ROOF && (
                <>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={params.productionOptimizerMode === OptimizerMode.Manual}
                                onChange={(e) =>
                                    updateParams({
                                        productionOptimizerMode: e.target.checked
                                            ? OptimizerMode.Manual
                                            : OptimizerMode.Auto,
                                    })
                                }
                            />
                        }
                        label={t('Manually optimize PV power')}
                    />
                    {params.productionOptimizerMode === OptimizerMode.Manual && (
                        <FormControl>
                            <FormLabel>{t('Target PV power (kWp)')}</FormLabel>
                            <Input
                                type="number"
                                value={params.targetKwp}
                                onChange={(e) => updateParams({ targetKwp: parseFloat(e.target.value) })}
                            />
                        </FormControl>
                    )}
                </>
            )}

            {!isFullFeedIn && (
                <>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={params.batteryOptimizerMode === OptimizerMode.Manual}
                                onChange={(e) =>
                                    updateParams({
                                        batteryOptimizerMode: e.target.checked
                                            ? OptimizerMode.Manual
                                            : OptimizerMode.Auto,
                                    })
                                }
                            />
                        }
                        label={t(
                            "Manually optimize battery capacity (for the funding optimizer, 'auto' still means the fixed factors)",
                        )}
                        style={{ marginTop: 0 }}
                    />
                    {params.batteryOptimizerMode === OptimizerMode.Manual && (
                        <FormControl>
                            <FormLabel>{t('Battery size factor (only fill if deviating)')}</FormLabel>
                            <Slider
                                step={0.1}
                                min={0}
                                max={2}
                                marks
                                valueLabelDisplay="on"
                                value={params.batteryKwpFactor}
                                onChange={(_, value) => updateParams({ batteryKwpFactor: value as number })}
                            />
                        </FormControl>
                    )}
                </>
            )}

            <FormControlLabel
                control={
                    <Switch
                        checked={params.constrainFullRoofTo100KWp}
                        onChange={(e) => updateParams({ constrainFullRoofTo100KWp: e.target.checked })}
                    />
                }
                label={t('Constrain PV system to 100 kWp')}
                style={{ marginTop: 0 }}
            />
        </Stack>
    );
}
